"use client";

import { useEffect, useMemo, useState } from "react";

import type { AccessContext } from "@sdk/access";
import { hasAnyCapability, hasCapability } from "@sdk/access/resolve-access-context";
import { SDK } from "@sdk/sdk";

import { useAccessOrEmpty } from "@/hooks/use-access";

import {
  loadSuppliersMap,
  mapBookingResult,
  mapCustomerInvoiceResult,
  mapCustomerPaymentReceiptResult,
  mapCustomerResult,
  mapEnquiryResult,
  mapQuoteResult,
  mapSupplierBillPaymentReceiptResult,
  mapSupplierBillResult,
} from "./global-search-mappers";
import {
  AccountingSearchQueryBuilder,
  GlobalBookingsSearchQueryBuilder,
  GlobalCustomersSearchQueryBuilder,
  GlobalEnquiriesSearchQueryBuilder,
  GlobalQuotesSearchQueryBuilder,
  parseEnquiryRefShortId,
} from "./global-search-query";
import type { GlobalSearchGroup, GlobalSearchResult, GlobalSearchResultType } from "./global-search.types";

const RESULT_LIMIT = 5;

const GROUP_LABELS: Record<GlobalSearchResultType, string> = {
  customer: "Customers",
  enquiry: "Enquiries",
  quote: "Quotes",
  booking: "Bookings",
  "customer-invoice": "Customer invoices",
  "supplier-bill": "Supplier bills",
  "customer-payment-receipt": "Customer payments",
  "supplier-bill-payment-receipt": "Supplier payments",
};

function shouldSearch(term: string) {
  const t = term.trim();
  if (t.length >= 2) return true;
  if (/^#\d+$/.test(t)) return true;
  if (/^qte-?\d+$/i.test(t)) return true;
  if (/^enq-?\d+$/i.test(t)) return true;
  if (/^bk-?\d+$/i.test(t)) return true;
  return false;
}

function getSearchSources(access: AccessContext) {
  const hasAccountingView = hasCapability(access, "VIEW_ACCOUNTING");
  const hasReportsView = hasCapability(access, "VIEW_REPORTS");
  const orgWideCrmSearch = hasAccountingView || hasReportsView;

  return {
    customers: true,
    enquiries: hasAnyCapability(access, [
      "MANAGE_ENQUIRIES",
      "VIEW_TEAM_ENQUIRIES",
    ]),
    quotes:
      hasAnyCapability(access, ["MANAGE_QUOTES", "VIEW_TEAM_QUOTES"]) ||
      orgWideCrmSearch,
    bookings:
      hasAnyCapability(access, [
        "MANAGE_BOOKINGS",
        "VIEW_ALL_BOOKINGS",
        "VIEW_TEAM_BOOKINGS",
      ]) || orgWideCrmSearch,
    customerInvoices: hasAccountingView || hasReportsView,
    accounting: hasAccountingView,
  };
}

async function searchCustomers(term: string): Promise<GlobalSearchResult[]> {
  const res = await SDK.queryContacts({
    query: GlobalCustomersSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0 },
  });

  return (res.docs ?? []).map(mapCustomerResult);
}

async function searchEnquiries(term: string): Promise<GlobalSearchResult[]> {
  const res = await SDK.opportunities.query({
    query: GlobalEnquiriesSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0 },
  });

  return (res.docs ?? []).map(mapEnquiryResult);
}

async function buildQuotesQuery(
  term: string
): Promise<Record<string, unknown>> {
  const trimmed = term.trim();
  if (!trimmed) return {};

  if (/^#?\s*enq-?\d+$/i.test(trimmed)) {
    const shortId = parseEnquiryRefShortId(trimmed);
    if (shortId == null) return { opportunityId: { $in: [] } };

    const enquiries = await SDK.opportunities.query({
      query: { shortId },
      options: { page: 1, limit: 20, offset: 0 },
    });
    const opportunityIds = (enquiries.docs ?? [])
      .map((enquiry) => enquiry.id)
      .filter(Boolean) as string[];

    return opportunityIds.length
      ? { opportunityId: { $in: opportunityIds } }
      : { opportunityId: { $in: [] } };
  }

  if (trimmed.startsWith("#")) {
    const num = Number(trimmed.slice(1).split(/\s+/)[0]);
    if (!Number.isNaN(num)) {
      const enquiries = await SDK.opportunities.query({
        query: { shortId: num },
        options: { page: 1, limit: 20, offset: 0 },
      });
      const opportunityIds = (enquiries.docs ?? [])
        .map((enquiry) => enquiry.id)
        .filter(Boolean) as string[];

      if (opportunityIds.length) {
        return { opportunityId: { $in: opportunityIds } };
      }
    }
  }

  return GlobalQuotesSearchQueryBuilder(trimmed);
}

async function searchQuotes(term: string): Promise<GlobalSearchResult[]> {
  const query = await buildQuotesQuery(term);
  const res = await SDK.holidayQuotesX.query({
    query,
    options: { page: 1, limit: RESULT_LIMIT, offset: 0 },
  });

  return (res.docs ?? []).map(mapQuoteResult);
}

async function searchBookings(term: string): Promise<GlobalSearchResult[]> {
  const res = await SDK.bookings.query({
    query: GlobalBookingsSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0 },
  });

  return (res.docs ?? []).map(mapBookingResult);
}

async function searchCustomerInvoices(
  term: string
): Promise<GlobalSearchResult[]> {
  const res = await SDK.customerInvoices.report({
    query: AccountingSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0, sortBy: ["-createdAt"] },
  });

  return (res.docs ?? []).map(mapCustomerInvoiceResult);
}

async function searchSupplierBills(
  term: string,
  suppliersMap: Record<string, { name?: string }>
): Promise<GlobalSearchResult[]> {
  const res = await SDK.supplierBills.report({
    query: AccountingSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0, sortBy: ["-createdAt"] },
  });

  return (res.docs ?? []).map((bill) => mapSupplierBillResult(bill, suppliersMap));
}

async function searchCustomerPaymentReceipts(
  term: string
): Promise<GlobalSearchResult[]> {
  const res = await SDK.customerPaymentReceipts.report({
    query: AccountingSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0, sortBy: ["-addedAt"] },
  });

  return (res.docs ?? []).map(mapCustomerPaymentReceiptResult);
}

async function searchSupplierBillPaymentReceipts(
  term: string
): Promise<GlobalSearchResult[]> {
  const res = await SDK.supplierBillPaymentReceipts.report({
    query: AccountingSearchQueryBuilder(term),
    options: { page: 1, limit: RESULT_LIMIT, offset: 0, sortBy: ["-addedAt"] },
  });

  return (res.docs ?? []).map(mapSupplierBillPaymentReceiptResult);
}

export function useGlobalSearch(searchText: string) {
  const access = useAccessOrEmpty();
  const sources = useMemo(() => getSearchSources(access), [access]);
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const term = searchText.trim();
    if (!shouldSearch(term)) {
      setGroups([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const runSearch = async () => {
      const suppliersMap = sources.accounting ? await loadSuppliersMap() : {};
      const tasks: Array<Promise<GlobalSearchResult[]>> = [];

      if (sources.customers) tasks.push(searchCustomers(term));
      if (sources.enquiries) tasks.push(searchEnquiries(term));
      if (sources.quotes) tasks.push(searchQuotes(term));
      if (sources.bookings) tasks.push(searchBookings(term));

      if (sources.customerInvoices) {
        tasks.push(searchCustomerInvoices(term));
      }

      if (sources.accounting) {
        tasks.push(
          searchSupplierBills(term, suppliersMap),
          searchCustomerPaymentReceipts(term),
          searchSupplierBillPaymentReceipts(term)
        );
      }

      const settled = await Promise.allSettled(tasks);
      if (cancelled) return;

      const byType = new Map<GlobalSearchResultType, GlobalSearchResult[]>();

      for (const result of settled) {
        if (result.status !== "fulfilled") continue;
        for (const item of result.value) {
          const existing = byType.get(item.type) ?? [];
          existing.push(item);
          byType.set(item.type, existing);
        }
      }

      const nextGroups = (
        Object.keys(GROUP_LABELS) as GlobalSearchResultType[]
      )
        .map((type) => ({
          type,
          label: GROUP_LABELS[type],
          results: byType.get(type) ?? [],
        }))
        .filter((group) => group.results.length > 0);

      setGroups(nextGroups);
      setIsLoading(false);
    };

    runSearch().catch(() => {
      if (!cancelled) {
        setGroups([]);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [searchText, sources]);

  const hasResults = groups.some((group) => group.results.length > 0);

  return { groups, isLoading, hasResults, sources };
}
